import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Globe, Server, Network, Hash,
    Fingerprint, Tag, Calendar, Loader2, Search,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ChevronUp, ChevronDown
} from "lucide-react";
import baseapi from "../../services/baseapi";
import { showError } from "../../utils/toast";
import { useClientLevel } from "../../hooks/useClientLevel";
import { ItemsPerPageDropdown } from "../../components/ItemsPerPageDropdown ";

interface ADDomain {
    id: number;
    created: string;
    updated: string;
    agent_id: number;
    client_id: number;
    asset_id: number;
    dc: string;
    domain: string;
    dns_host_name: string;
    operating_system: string;
    operating_system_version: string;
    distinguished_name: string;
    object_sid: string;
    object_guid: string;
}

export default function Domain({ asset_id }: { asset_id: any }) {
    const [domains, setDomains] = useState<ADDomain[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    const level = useClientLevel();

    // Debounce search — reset to page 1 on new search
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchDomains = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;

            if (debouncedSearch) {
                queryObj.$or = [
                    { domain: { $ilike: `%${debouncedSearch}%` } },
                    { dc: { $ilike: `%${debouncedSearch}%` } },
                    { dns_host_name: { $ilike: `%${debouncedSearch}%` } },
                    { distinguished_name: { $ilike: `%${debouncedSearch}%` } },
                    { object_sid: { $ilike: `%${debouncedSearch}%` } },
                ];
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * limit,
                limit
            };

            // Add API Sorting if requested
            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/ad_domain", { params });

            if (response.data.status) {
                setDomains(response.data.data);
                setTotalRecords(response.data.total ?? response.data.data.length);
            } else {
                showError("Failed to load domain data");
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    }, [level, debouncedSearch, currentPage, limit, sortConfig, asset_id]);

    useEffect(() => {
        fetchDomains();
    }, [fetchDomains]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
        setCurrentPage(1); // Reset to page 1 on sort
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey, icon }: { label: string; sortKey: string; icon?: React.ReactNode }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group select-none"
            >
                <div className="flex items-center gap-2">
                    {icon}
                    {label}
                    <div className="flex items-center ml-auto">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-blue-400" />
                            ) : (
                                <ChevronDown size={12} className="text-blue-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-gray-200" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    const Cell = ({ value }: { value: string }) => (
        <span className={!value || value === "-" ? " text-gray-200" : "text-gray-100"}>
            {value || "-"}
        </span>
    );

    const totalPages = Math.max(1, Math.ceil(totalRecords / limit));
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    return (
        <div className="flex flex-col bg-transparent text-gray-200">
            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex items-center gap-2">
                    <div className="bg-blue-600/20 p-1.5 rounded-lg border border-blue-500/30">
                        <Globe size={16} className="text-blue-400" />
                    </div>
                    <h1 className="text-lg font-bold text-white tracking-wide">Domain</h1>
                </div>

                <div className="flex items-center gap-2">
                    <div className="relative w-56 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200 group-focus-within:text-blue-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search domains..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-all"
                        />
                    </div>
                    <button
                        onClick={fetchDomains}
                        disabled={loading}
                        className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0 disabled:opacity-50"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-blue-400"} />
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                <SortableHeader label="DC" sortKey="dc" icon={<Tag size={13} className="text-blue-400" />} />
                                <SortableHeader label="Domain" sortKey="domain" icon={<Globe size={13} className="text-indigo-400" />} />
                                <SortableHeader label="DNS Hostname" sortKey="dns_host_name" icon={<Server size={13} className="text-cyan-400" />} />
                                <SortableHeader label="OS" sortKey="operating_system" icon={<Network size={13} className="text-emerald-400" />} />
                                <SortableHeader label="Distinguished Name" sortKey="distinguished_name" icon={<Hash size={13} className="text-yellow-400" />} />
                                <SortableHeader label="Object SID" sortKey="object_sid" icon={<Fingerprint size={13} className="text-purple-400" />} />
                                <SortableHeader label="Discovered" sortKey="created" icon={<Calendar size={13} className="text-orange-400" />} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-gray-500">
                                            <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Loading domain data...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : domains.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-40">
                                            <Globe size={24} className="text-gray-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">
                                                {debouncedSearch ? `No results for "${debouncedSearch}"` : "No domain data found"}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                domains.map((domain) => (
                                    <tr key={domain.id} className="hover:bg-gray-800/60 transition-colors group">
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded text-[10px] font-bold text-blue-300 uppercase tracking-wider">
                                                <Tag size={10} /> {domain.dc || "-"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[11px] font-bold text-gray-100">{domain.domain || "-"}</span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[11px] font-mono text-cyan-300">
                                                <Cell value={domain.dns_host_name} />
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex flex-col gap-0.5">
                                                <Cell value={domain.operating_system} />
                                                {domain.operating_system_version && domain.operating_system_version !== "-" && (
                                                    <span className="text-[9px] text-gray-500 font-mono">v{domain.operating_system_version}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 max-w-[220px]">
                                            <span className="text-[10px] font-mono text-gray-200 truncate block" title={domain.distinguished_name}>
                                                <Cell value={domain.distinguished_name} />
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[10px] font-mono text-purple-300/80">
                                                <Cell value={domain.object_sid} />
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[10px] font-mono text-gray-200">
                                                {domain.created
                                                    ? new Date(domain.created + "Z").toLocaleString("en-IN", {
                                                        timeZone: "Asia/Kolkata",
                                                    })
                                                    : "-"}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-gray-500 tracking-widest">
                            <span className="text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-gray-300">{endRecord}</span>
                            <span className="mx-1 text-gray-200">/</span>
                            <span className="text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="text-[9px] text-blue-400 hover:text-blue-300 font-bold uppercase tracking-widest transition-colors"
                            >
                                Clear search
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800/50 hover:bg-gray-700 rounded"
                        >
                            <ChevronsLeft size={14} />
                        </button>
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800/50 hover:bg-gray-700 rounded"
                        >
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button
                            disabled={currentPage >= totalPages}
                            onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800/50 hover:bg-gray-700 rounded"
                        >
                            <ChevronRight size={14} />
                        </button>
                        <button
                            disabled={currentPage >= totalPages}
                            onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800/50 hover:bg-gray-700 rounded"
                        >
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}